import { useCallback } from 'react';
import { useArtsUpload } from '~/adapters/services/accounts/arts/hooks';
import { useAccount } from '~/contexts/UserAccount/UserAccountContext';
import { pushErrorNotification } from '~/adapters/notistack/notistack';
import { FileRejection } from 'react-dropzone';

export const ACCEPTED_FILES_EXT = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'pdf', 'ai', 'psd', 'eps'];

export const ACCEPTED_MIME_TYPES =
    'image/jpeg, image/png, application/pdf, application/x-pdf, image/bmp, image/gif, ' +
    'application/vnd.adobe.illustrator, application/illustrator, image/vnd.adobe.illustrator, ' +
    'application/vnd.adobe.photoshop, application/photoshop, image/vnd.adobe.photoshop, ' +
    'application/postscript, image/x-eps';
export const FILE_MAX_SIZE_IN_BYTES = 5_000_000; // in bytes

export const useUpload = () => {
    const { mutate, isPending } = useArtsUpload();
    const currentAccount = useAccount();

    const handleRejectedFiles = useCallback((rejectedFiles: FileRejection[]) => {
        rejectedFiles.forEach((rejectedFile) => {
            let message = `File ${rejectedFile.file.name} failed to upload.`;

            if (rejectedFile.errors.some((err) => err.code === 'file-too-large')) {
                message += ` File is too large, please use file with maximal size ${(
                    FILE_MAX_SIZE_IN_BYTES / 1e6
                ).toFixed()} Mb.`;
            }

            if (rejectedFile.errors.some((err) => err.code === 'file-invalid-type')) {
                message += ` File has invalid type.`;
            }
            pushErrorNotification(<span data-action="file-too-large-alert">{message}</span>);
        });
    }, []);

    const onUpload = useCallback(
        (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
            handleRejectedFiles(rejectedFiles);
            mutate({ files: acceptedFiles, accountId: currentAccount.id });
        },
        [mutate, currentAccount.id, handleRejectedFiles],
    );

    return {
        isPending,
        onUpload,
    };
};
